import React, { useRef, useState } from 'react';
import JobWaitingModal from '../Components/Job/JobWaitingModal';
import { JobCompleteSocketData } from '../Components/Job/JobWaiting';
import useNotification from '../../../Hooks/useNotification.hook';
import useSocket from './useSocket';
import { Socket } from '../Enums/Socket.enum';

interface RenderJobWaitingProps {
	onJobSuccess: () => void;
	onJobFailed?: () => void;
	onJobSkipWaiting?: () => void;
	waitingMessage?: string;
	failedMessage?: string;
	timeout?: number;
	pollInterval?: number;
}

export default function useJob() {
	const jobIdsRef = useRef<string[]>([]);
	const [jobIds, setJobIds] = useState<string[]>([]);
	const [statusMessages, setStatusMessages] = useState<Record<string, string>>(
		{},
	);
	const { showNotificationAlert } = useNotification();

	useSocket(Socket.JOB_COMPLETED, (data: JobCompleteSocketData) => {
		const message = data.statusMessage;
		if (!message) return;
		setStatusMessages(prev =>
			prev[data.jobId] === message ? prev : { ...prev, [data.jobId]: message },
		);
	});

	const handleStopWaitingForJob = (jobId: string) => {
		jobIdsRef.current = jobIdsRef.current.filter(i => i !== jobId);
		setJobIds(jobIdsRef.current);
		setStatusMessages(prev => {
			const next = { ...prev };
			delete next[jobId];
			return next;
		});
		return jobIdsRef.current.length < 1;
	};

	const RenderJobWaiting: React.FC<RenderJobWaitingProps> = ({
		onJobSuccess,
		onJobFailed,
		onJobSkipWaiting,
		waitingMessage = 'component.jobWaiting.waitingTitle',
		failedMessage = 'component.jobWaiting.jobFailed',
		timeout = 10000,
		pollInterval = 10000,
	}) => {
		const handleOnJobSuccess = (jobId: string) => {
			const allJobsDone = handleStopWaitingForJob(jobId);
			if (allJobsDone) {
				onJobSuccess();
			}
		};

		const handleJobFailed = (jobId: string, message: string) => {
			showNotificationAlert({
				content: message,
			});
			handleStopWaitingForJob(jobId);
			onJobFailed?.();
		};

		const handleSkipWaitingForJob = (jobId: string) => {
			handleStopWaitingForJob(jobId);
			onJobSkipWaiting?.();
		};

		return (
			<>
				{jobIds.length > 0 &&
					jobIds.map(jobId => (
						<JobWaitingModal
							key={jobId}
							jobId={jobId}
							onClose={() => handleSkipWaitingForJob(jobId)}
							onJobSuccess={() => handleOnJobSuccess(jobId)}
							onJobFailed={() => handleJobFailed(jobId, failedMessage)}
							onJobChained={chainedJobId =>
								handleStartWaitingForJob(chainedJobId)
							}
							waitingMessage={statusMessages[jobId] ?? waitingMessage}
							timeout={timeout}
							pollInterval={pollInterval}
						/>
					))}
			</>
		);
	};

	const handleStartWaitingForJob = (jobId: string) => {
		if (jobIdsRef.current.includes(jobId)) return;
		jobIdsRef.current = [...jobIdsRef.current, jobId];
		setJobIds(jobIdsRef.current);
	};

	return {
		RenderJobWaiting,
		handleStartWaitingForJob,
	};
}
